/**
 * A aba Cadastros: o Plano de Contas e os Favorecidos.
 *
 * Papel: reproduz `#tab-cadastros` (l. 465–493): o `PlanoDeContas` em cima, a
 * `ListaDeFavorecidos` embaixo, e o "Exportar XLSX" dos cadastros
 * (`exportCadastrosXLSX`, l. 2841–2867), com as duas abas e o aviso
 * "✓ Cadastros exportados".
 *
 * Como usar: a aba lê o plano e os favorecidos sozinha; a casa do caixa vem de
 * `useCasaDoCaixa`. A exportação recusa, com "Aguarde os cadastros carregarem",
 * enquanto o plano ou os favorecidos não chegaram, em vez de gravar uma aba
 * vazia.
 */
import { acessoAlcanca } from '@concierge/shared';

import { useNotificacao } from '@/shared/embutido';
import { useAcesso } from '@/shared/hooks';
import { exportarPlanilha } from '@/shared/lib/planilha';
import { hojeEmIso } from '@/shared/lib/vencimento';
import { useCasaDoCaixa, useFavorecidos, usePlanoDeContas } from '../hooks/usar-financas';
import { planilhaDosCadastros } from '../planilhas';
import { ListaDeFavorecidos } from './lista-de-favorecidos';
import { PlanoDeContas } from './plano-de-contas';

/**
 * A aba Cadastros.
 *
 * @returns O plano de contas e os favorecidos.
 */
export function AbaCadastros() {
  const casa = useCasaDoCaixa();
  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'financas', 'editar');
  const notificar = useNotificacao();
  const plano = usePlanoDeContas();
  const favorecidos = useFavorecidos(casa.residenciaId);

  const semCaixa = casa.situacao === 'pronta' && casa.residenciaId === null;
  const listaDeFavorecidos = semCaixa ? [] : favorecidos.data;
  const favorecidosFalhou = casa.situacao === 'falhou' || favorecidos.isError;

  /** Exporta o plano e os favorecidos para o Excel. */
  async function handleExportar() {
    if (plano.data === undefined || listaDeFavorecidos === undefined) {
      notificar('Aguarde os cadastros carregarem', false);
      return;
    }
    try {
      await exportarPlanilha(
        planilhaDosCadastros(plano.data, listaDeFavorecidos, hojeEmIso()),
      );
      notificar('✓ Cadastros exportados');
    } catch {
      notificar(
        'A planilha não pôde ser gerada. Verifique a conexão com a internet e tente novamente.',
        false,
      );
    }
  }

  return (
    <div>
      <PlanoDeContas
        plano={plano.data}
        falhou={plano.isError}
        onTentarDeNovo={() => void plano.refetch()}
        podeEditar={podeEditar}
        onExportar={() => void handleExportar()}
      />
      <ListaDeFavorecidos
        favorecidos={listaDeFavorecidos}
        falhou={favorecidosFalhou}
        onTentarDeNovo={
          casa.situacao === 'falhou' ? casa.tentarDeNovo : () => void favorecidos.refetch()
        }
        podeEditar={podeEditar}
        residenciaId={casa.residenciaId}
        plano={plano.data ?? []}
      />
    </div>
  );
}
